import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ConfirmationBox from "../../components/ConfirmationBox";
import ErrorBox from "../../components/ErrorBox";
import classes from "./Profile.module.css";
import useLanguage from "../../utils/useLanguage";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import SectionDivider from "../../components/SectionDivider";
import PageWrapper from "../../components/PageWrapper";
import AreYouSureModal from "../../components/AreYouSureModal";
import { resolveIcon } from "../../utils/resolveIcon";
const CustomerProfile = ({ setIsAuthenticated }) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loading, setLoading] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [passwordFields, setPasswordFields] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordChanged, setPasswordChanged] = useState({
    try: false,
    success: false,
  });
  const [backendError, setBackendError] = useState(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState("");
  const [userType, setUserType] = useState(null);

  const [organizationData, setOrganizationData] = useState({
    orgSubType: "",
    orgPhoneNumber: "",
    orgEmail: "",
    orgAddress: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [userData, setUserData] = useState({
    userId: "",
    username: "",
    email: "",
    phoneNumber: "",
    address: "",
  });

  // ! TEMP
  const user = {};

  // * START of handleLogout function *
  const handleLogout = () => {
    const storedUserType = localStorage.getItem("userType");

    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("supplierId");
    localStorage.removeItem("customerId");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("phoneNumber");
    localStorage.removeItem("SuperAdminToken");
    localStorage.removeItem("organizationId");
    localStorage.removeItem("userType");
    localStorage.removeItem("organizationType");
    localStorage.removeItem("organizationName");
    localStorage.removeItem("orgPhoneNumber");
    localStorage.removeItem("orgEmail");
    localStorage.removeItem("subscriptionType");
    localStorage.removeItem("address");
    localStorage.removeItem("invoiceType");

    if (setIsAuthenticated) {
      setIsAuthenticated(null);
    }

    if (storedUserType === "admin" || storedUserType === "superadmin") {
      navigate("/admin-login", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    const id = parseInt(localStorage.getItem("userId"), 10);
    const email = localStorage.getItem("email");
    const username = localStorage.getItem("username");
    const phoneNumber = localStorage.getItem("phoneNumber");
    const address = localStorage.getItem("address") || ""; // Add address
    const typeOfUser = localStorage.getItem("userType");
    setUserType(typeOfUser);

    if (typeOfUser === "organization") {
      setOrganizationData(() => ({
        orgName: localStorage.getItem("username"),
        orgId: localStorage.getItem("organizationId"),
        orgSubType: localStorage.getItem("subscriptionType"),
        orgPhoneNumber: localStorage.getItem("orgPhoneNumber"),
        orgEmail: localStorage.getItem("orgEmail"),
        orgAddress: localStorage.getItem("address") || "", // Add org address
      }));
    }
    setUserData({ userId: id, username, email, phoneNumber, address });
  }, []);

  useEffect(() => {
    if (changePassword) {
      // Reset errors when user starts changing password
      setBackendError(false);
      setBackendErrorMessage("");
    }
  }, [changePassword]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (userType === "organization") {
      // Update both userData and organizationData for organization users
      setUserData((prev) => ({ ...prev, [name]: value }));

      // Update organizationData as well for display purposes
      if (name === "email") {
        setOrganizationData((prev) => ({ ...prev, orgEmail: value }));
      } else if (name === "phoneNumber") {
        setOrganizationData((prev) => ({ ...prev, orgPhoneNumber: value }));
      } else if (name === "address") {
        setOrganizationData((prev) => ({ ...prev, orgAddress: value }));
      } else if (name === "orgName") {
        setOrganizationData((prev) => ({ ...prev, orgName: value }));
      }
    } else {
      setUserData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toggleChangePassword = () => {
    setChangePassword(!changePassword);
    setPasswordFields({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordFields({ ...passwordFields, [name]: value });
  };

  const cancelChangePassword = () => {
    toggleChangePassword();
    setBackendError(false);
    setBackendErrorMessage("");
  };

  const savePasswordChanges = async () => {
    // Reset previous errors
    setBackendError(false);
    setBackendErrorMessage("");

    // Check for empty fields
    if (
      !passwordFields.currentPassword ||
      !passwordFields.newPassword ||
      !passwordFields.confirmPassword
    ) {
      setBackendError(true);
      setBackendErrorMessage(t("FillInAllPasswordFields"));
      return;
    }

    // Check if new password and confirm password match
    if (passwordFields.newPassword !== passwordFields.confirmPassword) {
      setBackendError(true);
      setBackendErrorMessage(t("NewPasswordsDoNotMatch"));
      return; // Exit if passwords do not match
    }

    setBackendError(false);

    if (passwordFields.newPassword === passwordFields.confirmPassword) {
      try {
        setLoading("changePass");
        let response;
        if (userType === "organization") {
          response = await axios.post(
            `${process.env.REACT_APP_URL}/api/org/changePassword`,
            {
              passwords: passwordFields,
            },
            {
              params: {
                orgId: organizationData.orgId,
              },
            },
          );
        } else {
          response = await axios.post(
            `${process.env.REACT_APP_URL}/user/${userData.userId}/changepass`, // Use userData instead of user
            {
              passwords: passwordFields,
            },
          );
        }
        setPasswordChanged({ try: true, success: true });
      } catch (error) {
        setPasswordChanged({ try: true, success: false });
        const errorMessage = error.response?.data?.error || "An error occurred";
        setBackendErrorMessage(t(errorMessage));
      } finally {
        setLoading("");
      }

      toggleChangePassword();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      setLoading("editUser");

      let response;

      if (userType === "organization") {
        // Call organization endpoint
        response = await axios.patch(`${process.env.REACT_APP_URL}/api/org`, {
          id: organizationData.orgId,
          organizationName: organizationData.orgName,
          email: organizationData.orgEmail,
          phoneNumber: organizationData.orgPhoneNumber,
          address: organizationData.orgAddress, // Make sure to add address to userData state
        });
      } else {
        // Call user endpoint
        response = await axios.patch(`${process.env.REACT_APP_URL}/editUser`, {
          id: userData.userId,
          username: userData.username,
          email: userData.email,
          phoneNumber: userData.phoneNumber,
          address: userData.address,
        });
      }

      if (response?.data) {
        if (userType === "organization") {
          // Update organization data in localStorage
          setUserData((prev) => ({
            ...prev,
            username: response.data.organizationName,
            email: response.data.email,
            phoneNumber: response.data.phoneNumber,
            address: response.data.address,
          }));

          localStorage.setItem("userId", response.data.id);
          localStorage.setItem("orgEmail", response.data.email);
          localStorage.setItem("username", response.data.organizationName);
          localStorage.setItem("orgPhoneNumber", response.data.phoneNumber);
          localStorage.setItem("address", response.data.address);

          // Update organizationData state as well
          setOrganizationData((prev) => ({
            ...prev,
            orgEmail: response.data.email,
            orgPhoneNumber: response.data.phoneNumber,
          }));
        } else {
          // Update user data in localStorage
          setUserData({
            userId: response.data.id,
            username: response.data.username,
            email: response.data.email,
            phoneNumber: response.data.phoneNumber,
            address: response.data.address,
          });
          localStorage.setItem("userId", response.data.id);
          localStorage.setItem("email", response.data.email);
          localStorage.setItem("username", response.data.username);
          localStorage.setItem("phoneNumber", response.data.phoneNumber);
          localStorage.setItem("address", response.data.address);
        }

        setEditMode(false);
      }
    } catch (error) {
      console.error("Failed to update data", error);
      const errorMessage =
        error.response?.data?.error || "An unknown error occurred";
      setBackendError(true);
      setBackendErrorMessage(errorMessage);
    } finally {
      setLoading("");
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  return (
    <PageWrapper className={classes.profileContainer} >
      {showLogoutModal && (
        <AreYouSureModal
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogout}
          confirmTitle="logout"
          customMessage="areYouSureLogout"
          confirmIconType="logout"
        />
      )}
      <section>
        <div className={classes.profileCard}>
          <section className={classes.header}>
            <h2 className={classes.profileGreeting}>
              <span>{t("hello")}</span>
              <span className={classes.name}>{userData.username}</span>
            </h2>
            <img
              src={resolveIcon("profileCover")}
              alt="profile cover"
              className={classes.coverImage}
            />
          </section>
          <section className={classes.content}>
            <img
              src={resolveIcon("profilePicture")}
              alt="profile"
              className={
                currentDirection === "ltr" ? classes.avatar : classes.avatarRtl
              }
            />
            <div
              className={
                currentDirection === "ltr"
                  ? classes.profileActions
                  : classes.profileActionsRtl
              }
            >
              {editMode ? (
                <div className={classes.buttonsContainer}>
                  <CustomButton
                    title={t("cancel")}
                    onClick={toggleEditMode}
                    variant="blank"
                    icon="close"
                    iconSize={14}
                  />
                  <CustomButton
                    onClick={handleSubmit}
                    title={t("confirm")}
                    icon="save"
                    iconSize={16}
                    disabled={loading === "editUser"}
                  />
                </div>
              ) : (
                <CustomButton
                  title={t("editProfile")}
                  variant="blank"
                  icon="edit"
                  iconSize={20}
                  onClick={toggleEditMode}
                />
              )}
            </div>
            {loading === "editUser" && <Loader />}
            <section className="o-column u-padding--right_700 u-padding--left_700">
              <SectionDivider title={t("generalInfo")} />
              {editMode ? (
                <div className={classes.editingMode}>
                  {userType === "organization" ? (
                    <div className={classes.inputContainer}>
                      <label className={classes.inputLabel} htmlFor="orgName">
                        {t("organizationName")}
                      </label>
                      <CustomInput
                        type="text"
                        id="orgName"
                        name="orgName"
                        value={organizationData.orgName}
                        onChange={(e) =>
                          setOrganizationData((prev) => ({
                            ...prev,
                            orgName: e.target.value,
                          }))
                        }
                        thick
                      />
                    </div>
                  ) : (
                    <div className={classes.inputContainer}>
                      <label className={classes.inputLabel} htmlFor="username">
                        {t("fullName")}
                      </label>
                      <CustomInput
                        type="text"
                        id="username"
                        name="username"
                        value={userData?.username}
                        onChange={handleInputChange}
                        thick
                      />
                    </div>
                  )}
                  <div className={classes.inputContainer}>
                    <label className={classes.inputLabel} htmlFor="email">
                      {t("email")}
                    </label>
                    <CustomInput
                      type="email"
                      id="email"
                      name="email"
                      value={
                        userType == "organization"
                          ? organizationData.orgEmail
                          : userData?.email
                      }
                      onChange={handleInputChange}
                      thick
                      readonly={true}
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <label className={classes.inputLabel} htmlFor="phoneNumber">
                      {t("phoneNumber")}
                    </label>
                    <CustomInput
                      type="text"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={
                        userType === "organization"
                          ? organizationData.orgPhoneNumber
                          : userData.phoneNumber
                      }
                      onChange={handleInputChange}
                      thick
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <label className={classes.inputLabel} htmlFor="phoneNumber">
                      {t("address")}
                    </label>
                    <CustomInput
                      type="text"
                      id="address"
                      name="address"
                      value={
                        userType === "organization"
                          ? organizationData.orgAddress
                          : userData.address
                      }
                      onChange={handleInputChange}
                      thick
                    />
                  </div>
                </div>
              ) : (
                <div className={classes.infoGrid}>
                  <div className={classes.infoCard}>
                    <span className={classes.infoLabel}>{t("fullName")}</span>
                    <span className={classes.infoValue}>
                      {userType === "organization"
                        ? organizationData.orgName
                        : userData.username}
                    </span>
                  </div>
                  <div className={classes.infoCard}>
                    <span className={classes.infoLabel}>{t("email")}</span>
                    <span className={classes.infoValue}>
                      {userType === "organization"
                        ? organizationData.orgEmail
                        : userData.email}
                    </span>
                  </div>
                  <div className={classes.infoCard}>
                    <span className={classes.infoLabel}>
                      {t("phoneNumber")}
                    </span>
                    <span className={classes.infoValue}>
                      {userType === "organization"
                        ? organizationData.orgPhoneNumber
                        : userData.phoneNumber}
                    </span>
                  </div>
                  <div className={classes.infoCard}>
                    <span className={classes.infoLabel}>{t("address")}</span>
                    <span className={classes.infoValue}>
                      {userType === "organization"
                        ? organizationData.orgAddress
                        : userData.address}
                    </span>
                  </div>

                  <div className={classes.infoCard}>
                    <span className={classes.infoLabel}>
                      {t("organizationType")}
                    </span>
                    <span className={classes.infoValue}>
                      {t(localStorage.getItem("organizationType")) || "N/A"}
                    </span>
                  </div>

                  <div className={classes.infoCard}>
                    <span className={classes.infoLabel}>
                      {t("role")}
                    </span>
                    <span className={classes.infoValue}>
                      {t(localStorage.getItem("role")) || "-"}
                    </span>
                  </div>
                </div>
              )}
            </section>

            {/* Subscription section */}
            {  userType === "organization" &&  <section className="o-column u-padding--right_700 u-padding--left_700">
              <SectionDivider title={t("subscription")} />

               <div className={classes.subscriptionBadge}>
                <span>{t("subscriptionType")}:</span>
                <span>
                  {t(organizationData.orgSubType)}

                </span>


              </div>
            </section>}

            <section className="o-column u-padding--right_700 u-padding--left_700">
              {changePassword ? (
                <div className={classes.changePasswordSection}>
                  <div className={classes.changePasswordSection}>
                    <SectionDivider title={t("changePassword")} />
                    <div className={classes.inputContainer}>
                      <label
                        className={classes.inputLabel}
                        htmlFor="currentPassword"
                      >
                        {t("currentPassword")}
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordFields.currentPassword}
                        onChange={handlePasswordChange}
                      />
                    </div>
                    <div className={classes.inputContainer}>
                      <label
                        className={classes.inputLabel}
                        htmlFor="newPassword"
                      >
                        {t("newPassword")}
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordFields.newPassword}
                        onChange={handlePasswordChange}
                      />
                    </div>
                    <div className={classes.inputContainer}>
                      <label
                        className={classes.inputLabel}
                        htmlFor="confirmPassword"
                      >
                        {t("confirmNewPassword")}
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordFields.confirmPassword}
                        onChange={handlePasswordChange}
                      />
                    </div>

                    {backendError && <ErrorBox>{backendErrorMessage}</ErrorBox>}

                    {loading === "changePass" ? <Loader /> : null}
                    <div className="o-row o-row--horizontal-align_end o-row--gap-size_300">
                      <CustomButton
                        title={t("cancel")}
                        variant="blank"
                        onClick={cancelChangePassword}
                        icon="close"
                        iconSize={14}
                      />
                      <CustomButton
                        title={t("confirm")}
                        onClick={savePasswordChanges}
                        icon="save"
                        iconSize={16}
                        disabled={loading === "changePass"}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className={classes.changePasswordSection}>
                  <SectionDivider title={t("changePassword")} />

                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className={classes.primaryButton}>
                      <CustomButton
                        title={t("changePassword")}
                        variant="blank"
                        icon="resetPassword"
                        iconSize={24}
                        onClick={toggleChangePassword}
                      />
                    </div>
                  </div>

                  {passwordChanged.try &&
                    (passwordChanged.success ? (
                      <ConfirmationBox>{t("passwordChanged")}</ConfirmationBox>
                    ) : (
                      <ErrorBox>{backendErrorMessage}</ErrorBox>
                    ))}
                </div>
              )}
            </section>

            <section className="o-column u-padding--right_700 u-padding--left_700">
              <div className={classes.changePasswordSection}>
                <SectionDivider title={t("logout")} />

                <div style={{ display: "flex", gap: "10px" }}>
                  <div className={classes.primaryButton}>
                    <CustomButton
                      title={t("logout")}
                      variant="blank"
                      icon="logoutP"
                      iconSize={24}
                      onClick={() => setShowLogoutModal(true)}
                    />
                  </div>
                </div>
              </div>
            </section>
          </section>
        </div>
      </section>
    </PageWrapper>
  );
};

export default CustomerProfile;
